import { useGetUserAccountDescriptor } from "@/app/(core)/ib/[subject]/components/banners/RestrictedResourceBanner";
import { unixDayToDate } from "@/lib/redux/helpers";
import { prettyDate } from "@/lib/stripe/formatters";
import { averageArray } from "@/lib/utils/helpers";
import {
  CramSheetReduxSchema,
  FlashcardReduxSchema,
  NoteReduxSchema,
  QuestionReduxSchema,
  SubTopicReduxSchema,
  TopicTestReduxSchema,
  UnixDay,
  VideoReduxSchema,
} from "@/types/redux-schema";
import { Resources } from "@/types/subject-schema";
import { Badge, useMantineTheme } from "@mantine/core";
import { CheckCircleRounded } from "@mui/icons-material";
import clsx from "clsx";
import { CSSProperties } from "react";
import classes from "./ProgressTrackerChip.module.css";

export default function ProgressTrackerChip({
  resource,
  disableActions,
  subjectName,
  subjectDocumentId,
  resourceReduxMeta,
  noResource,
}: {
  resource: Extract<
    Resources,
    | "topic tests"
    | "cram sheets"
    | "videos"
    | "notes"
    | "flashcards"
    | "questions"
  >;
  disableActions: boolean;
  subjectName: string;
  subjectDocumentId: string;
  resourceReduxMeta:
    | SubTopicReduxSchema["cramSheets"]
    | VideoReduxSchema
    | NoteReduxSchema
    | FlashcardReduxSchema
    | QuestionReduxSchema
    | TopicTestReduxSchema;
  noResource: boolean;
}) {
  const accountDescriptor = useGetUserAccountDescriptor(
    subjectDocumentId,
    subjectName,
    resource
  );
  const theme = useMantineTheme();
  const lastActionedDates = getLastActionedDates(resourceReduxMeta);
  const actionedCount = lastActionedDates.length;
  const baseColor = noResource
    ? theme.colors.dark[5]
    : disableActions
    ? theme.colors.incorrect[5]
    : actionedCount > 0
    ? theme.colors.correct[5]
    : theme.colors.dark[5];
  const isFullWidth = ["topic tests", "cram sheets", "videos"].includes(
    resource
  );

  return (
    <Badge
      className={clsx(
        classes.chip,
        isFullWidth && classes.chipFullWidth,
        noResource && classes.chipNoResource
      )}
      size="lg"
      style={{ "--chip-base-color": baseColor } as CSSProperties}
      rightSection={
        noResource || disableActions || actionedCount <= 0 ? undefined : (
          <CheckCircleRounded
            style={{
              marginLeft: 4,
              display: "block",
              width: 18,
              height: 18,
            }}
          />
        )
      }
    >
      {getProgressTrackerContent(
        accountDescriptor,
        disableActions,
        resource,
        resourceReduxMeta,
        actionedCount,
        lastActionedDates
      )}
    </Badge>
  );
}

function getProgressTrackerContent(
  accountDescriptor: string | undefined,
  disableActions: boolean,
  resource: Extract<
    Resources,
    | "topic tests"
    | "cram sheets"
    | "videos"
    | "notes"
    | "flashcards"
    | "questions"
  >,
  resourceReduxMeta:
    | SubTopicReduxSchema["cramSheets"]
    | VideoReduxSchema
    | NoteReduxSchema
    | FlashcardReduxSchema
    | QuestionReduxSchema
    | TopicTestReduxSchema,
  actionedCount: number,
  lastActionedDates: UnixDay[]
) {
  if (!accountDescriptor) {
    return "Loading progress...";
  } else if (disableActions) {
    return `Progress tracking disabled`;
  } else {
    return getProgressTrackerPhrasingForResource(
      resource,
      actionedCount,
      lastActionedDates,
      resourceReduxMeta
    );
  }
}

function getLastActionedDates(
  resourceReduxMeta:
    | SubTopicReduxSchema["cramSheets"]
    | NoteReduxSchema
    | FlashcardReduxSchema
    | QuestionReduxSchema
    | VideoReduxSchema
    | TopicTestReduxSchema
) {
  // look for dates in tests and questions as dates attempted
  if (resourceReduxMeta && "da" in resourceReduxMeta) {
    return (resourceReduxMeta as TopicTestReduxSchema | QuestionReduxSchema).da;
  }
  // for videos and flashcards, look for dates in videos and flashcards as dates viewed
  if (resourceReduxMeta && "dv" in resourceReduxMeta) {
    return (resourceReduxMeta as VideoReduxSchema | FlashcardReduxSchema).dv;
  }
  // for notes, look for dates in notes as dates read. Even though we track each page we generate a chip for each page so its split out at page level before reaching this function
  if (resourceReduxMeta && "dr" in resourceReduxMeta) {
    return (resourceReduxMeta as NoteReduxSchema).dr;
  }
  // handle cram sheets differently as we track each one individually and never split out progress badge
  return (Object.values(resourceReduxMeta ?? {}) as CramSheetReduxSchema[]).map(
    (resourceMeta) => resourceMeta.dd.slice(-1)[0]
  );
}

function getProgressTrackerPhrasingForResource(
  resource: Extract<
    Resources,
    | "cram sheets"
    | "videos"
    | "notes"
    | "flashcards"
    | "questions"
    | "topic tests"
  >,
  actionedCount: number,
  lastActionedDates: UnixDay[],
  resourceReduxMeta:
    | SubTopicReduxSchema["cramSheets"]
    | VideoReduxSchema
    | NoteReduxSchema
    | FlashcardReduxSchema
    | QuestionReduxSchema
    | TopicTestReduxSchema
) {
  const lastActionedDate = prettyDate(
    unixDayToDate(Math.max(...(lastActionedDates as number[])) as UnixDay),
    true,
    true
  );
  const averagePerformance = getAveragePerformance(resourceReduxMeta);
  switch (resource) {
    case "cram sheets":
      if (actionedCount === 0) {
        return "Never downloaded";
      }
      return `${actionedCount} download${
        actionedCount > 1 ? "s" : ""
      }, last ${lastActionedDate}`;
    case "topic tests":
    case "questions":
      if (actionedCount === 0) {
        return "Never attempted";
      } else {
        return `${
          averagePerformance !== undefined ? `Avg: ${averagePerformance}%` : ""
        } (${actionedCount} attempt${
          actionedCount > 1 ? "s" : ""
        }, last ${lastActionedDate})`;
      }
    case "videos":
      if (actionedCount === 0) {
        return "Never viewed";
      } else {
        return `${actionedCount} view${
          actionedCount > 1 ? "s" : ""
        }, last ${lastActionedDate}`;
      }
    case "notes":
      if (actionedCount === 0) {
        return "Never read";
      } else {
        return `${actionedCount} read${
          actionedCount > 1 ? "s" : ""
        }, last ${lastActionedDate}`;
      }
    case "flashcards":
      if (actionedCount === 0) {
        return "Never reviewed";
      } else {
        return `${actionedCount} review${
          actionedCount > 1 ? "s" : ""
        }, last ${lastActionedDate}`;
      }
  }
}

function getAveragePerformance(
  resourceReduxMeta: Parameters<typeof getProgressTrackerPhrasingForResource>[3]
) {
  if (!resourceReduxMeta) {
    return undefined;
  }
  // if it's a topic test, return the average performance
  if ("perf" in resourceReduxMeta) {
    return (
      averageArray((resourceReduxMeta as TopicTestReduxSchema).perf) ??
      undefined
    );
  }
  return (
    averageArray(
      Object.values(resourceReduxMeta).flatMap(
        (
          resourceMeta:
            | CramSheetReduxSchema
            | VideoReduxSchema
            | NoteReduxSchema
            | FlashcardReduxSchema
            | QuestionReduxSchema
        ) => {
          if (typeof resourceMeta === "object" && "perf" in resourceMeta) {
            return averageArray(resourceMeta.perf) ?? [];
          }
          return [];
        }
      )
    ) ?? undefined
  );
}
